import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useGetFlows, getGetFlowQueryOptions, type KdsFlowDetail } from '@ordr-as/ordr-service-types/epos';
import { buildFlowGraph } from '../pages/KdsBoard/selectors';
import { EMPTY_FLOW_GRAPH, type FlowGraph } from '../pages/KdsBoard/types';

const FLOW_STALE_TIME = 5 * 60_000;

/**
 * Fetches all KDS flows (with details) for the location and builds the lookup
 * graph used to resolve a task's station and its upstream dependencies.
 * Flows change rarely, so they are cached aggressively.
 */
export function useKdsFlowGraph(locationId: number): { graph: FlowGraph; isPending: boolean } {
  const flows = useGetFlows(locationId, { query: { staleTime: FLOW_STALE_TIME } });

  const flowIds = useMemo(
    () => (flows.data ?? []).map((f) => f.id).filter((id): id is number => id !== undefined),
    [flows.data]
  );

  const details = useQueries({
    queries: flowIds.map((flowId) =>
      getGetFlowQueryOptions(locationId, flowId, { query: { staleTime: FLOW_STALE_TIME } })
    ),
    combine: (results) => ({
      data: results.map((r) => r.data).filter((d): d is KdsFlowDetail => d !== undefined),
      isPending: results.some((r) => r.isPending),
    }),
  });

  const graph = useMemo(
    () => (details.data.length > 0 ? buildFlowGraph(details.data) : EMPTY_FLOW_GRAPH),
    [details.data]
  );

  return { graph, isPending: flows.isPending || details.isPending };
}
